import { useWindowSize } from "../../hooks/use-window-size";
import classNames from "classnames";
import { NodeTitleAndValue } from "./FlowNodeTitleAndValue";
import { NodeImage } from "./FlowNodeImage";
import { NodeBtnMoreInfo } from "./FlowNodeBtnMoreInfo";
import { NodeSetpoint } from "./FlowNodeSetpoint";
import { Tooltip } from "primereact/tooltip";
import { formatDistanceStrict } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";

export interface EnergyFlowNodeProps {
  value?: number;
  unit?: string;
  name?: string;
  icon?: string;
  setpoint?: number;
  charge?: number;
  color?: string;
  isConnected?: boolean;
  isEV?: boolean;
  time?: Date;
  referenceTime?: Date;
  onSelect?: () => void;
}

export function EnergyFlowNode({
  value,
  unit = "W",
  name,
  icon,
  setpoint,
  charge,
  isConnected,
  isEV,
  time,
  referenceTime,
  onSelect,
}: EnergyFlowNodeProps) {
  const windowSize = useWindowSize();

  return (
    <div
      className={classNames(
        "group z-20 relative whitespace-nowrap m-2 rounded-3xl bg-white shadow-md hover:shadow-lg duration-100 cursor-pointer",
        windowSize.lg ? "text-xl w-max p-4 mx-4" : "text-sm p-3 m-0",
        onSelect ? "select-none hover:scale-105" : "shadow-md",
      )}
      onClick={onSelect}
    >
      {windowSize.lg ? (
        <div className="flex justify-center h-full overflow-hidden">
          {isConnected === false ? (
            <div className="absolute inset-0 z-10 bg-white opacity-70 rounded-3xl pointer-events-none"></div>
          ) : (
            <></>
          )}
          {setpoint != null && (
            <NodeSetpoint
              setpoint={setpoint}
              unit={unit}
              isConnected={isConnected}
              isEV={isEV}
            ></NodeSetpoint>
          )}

          {icon && <NodeImage isCenter={false} icon={icon} />}
          <div className="flex flex-col items-start">
            {name && value != null && (
              <NodeTitleAndValue
                name={name}
                unit={unit}
                value={value}
                charge={charge}
              ></NodeTitleAndValue>
            )}
            <NodeBtnMoreInfo />
          </div>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          <div className="flex justify-center w-full">
            {setpoint != null && (
              <NodeSetpoint
                setpoint={setpoint}
                unit={unit}
                isConnected={isConnected}
                isEV={isEV}
              ></NodeSetpoint>
            )}
            {isConnected === false ? (
              <div className="absolute inset-0 z-10 bg-white opacity-70 rounded-3xl pointer-events-none"></div>
            ) : (
              <></>
            )}
          </div>
          {name && value != null && (
            <NodeTitleAndValue
              name={name}
              unit={unit}
              value={value}
              charge={charge}
            ></NodeTitleAndValue>
          )}
          {icon && <NodeImage isCenter={false} icon={icon} />}
          <NodeBtnMoreInfo />
        </div>
      )}
      {time && referenceTime && (
        <Indicator time={time} referenceTime={referenceTime} isEv={isEV} />
      )}
    </div>
  );
}

let customId = 0;
function Indicator({
  time,
  referenceTime,
  isEv,
}: {
  time: Date;
  referenceTime: Date;
  isEv?: boolean;
}) {
  const referenceNowTimeAdjustment = useMemo(() => {
    return +referenceTime - Date.now();
  }, [referenceTime]);

  const prepareTooltipText = useCallback(() => {
    return (
      <div className="whitespace-nowrap">
        {formatDistanceStrict(time, referenceTime, { addSuffix: true })} -
        server
        <br />
        {formatDistanceStrict(time, Date.now() + referenceNowTimeAdjustment, {
          addSuffix: true,
        })}{" "}
        - here
        <br />
        {time.toLocaleString()}
      </div>
    );
  }, [time, referenceTime, referenceNowTimeAdjustment]);

  let myId = useMemo(() => customId++, []);
  let [open, setOpen] = useState(false);
  const [tooltipText, setTooltipText] = useState(prepareTooltipText());

  useEffect(() => {
    setTooltipText(prepareTooltipText());
    const timer = setInterval(() => {
      setTooltipText(prepareTooltipText());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [open, prepareTooltipText]);

  const color = useMemo(() => {
    const delay = +referenceTime - +time;
    if (delay < 0) return "bg-blue-400";
    if (isEv) {
      if (delay < 35e3) return "bg-primary";
      if (delay < 65e3) return "bg-orange-400";
      return "bg-red-600";
    }
    if (delay < 8e3) return "bg-primary";
    if (delay < 20e3) return "bg-orange-400";
    return "bg-red-600";
  }, [referenceTime, time, isEv]);

  return (
    <>
      <Tooltip
        position="top"
        target={`.tooltip-indicator-${myId}`}
        style={{ zIndex: 99999 }}
        onShow={() => {
          setTooltipText(prepareTooltipText());
          setOpen(true);
        }}
        onHide={() => setOpen(false)}
      >
        {tooltipText}
      </Tooltip>
      <div
        className={`tooltip-indicator-${myId} absolute -right-3 max-lg:-bottom-3 lg:-top-3  p-3 z-20`}
      >
        <div
          className={classNames(
            "absolute w-4 h-4 animate-ping rounded-full opacity-75",
            color,
          )}
        />
        <div className={classNames("w-4 h-4 rounded-full", color)} />
      </div>
    </>
  );
}
